import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

function initialsFor(user) {
  const source = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '?';
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
}

function displayNameFor(user) {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email;
}

function RoleBadge({ user }) {
  if (user.superadmin) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Superadmin
      </span>
    );
  }
  const palette = user.user_type === 'group_manager'
    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const label = user.user_type === 'group_manager' ? 'Group Manager' : 'Student';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${palette}`}>
      {label}
    </span>
  );
}

export default function SuperadminUsers({ users = [], filters = {} }) {
  const [emailQuery, setEmailQuery] = useState(filters.email || '');
  const [pendingId, setPendingId] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    router.get('/superadmin/users', { email: emailQuery }, { preserveState: true, preserveScroll: true });
  };

  const handleClear = () => {
    setEmailQuery('');
    router.get('/superadmin/users', {}, { preserveState: true, preserveScroll: true });
  };

  const handleImpersonate = (user) => {
    setPendingId(`impersonate-${user.id}`);
    router.post(`/superadmin/users/${user.id}/impersonate`, {}, {
      onFinish: () => setPendingId(null),
    });
  };

  const total = users.length;
  const hasFilter = (filters.email || '').trim().length > 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Users</h1>
            <p className="mt-1 text-sm text-gray-500">Manage all portal accounts.</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={emailQuery}
                onChange={(e) => setEmailQuery(e.target.value)}
                placeholder="Search by email..."
                className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition cursor-pointer"
              >
                Search
              </button>
              {hasFilter && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {hasFilter ? 'Matching users' : 'All users'}
            </h2>
            <span className="ml-auto text-sm text-gray-400">
              {total} {total === 1 ? 'person' : 'people'}
              {total === 100 && ' (showing first 100)'}
            </span>
          </div>

          {total === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </div>
              <p className="text-gray-600">
                {hasFilter ? 'No users match that search.' : 'No users found.'}
              </p>
              {hasFilter && (
                <button
                  onClick={handleClear}
                  className="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {users.map((user) => (
                <div key={user.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {initialsFor(user)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 truncate">{displayNameFor(user)}</p>
                        <RoleBadge user={user} />
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        <span className="font-mono text-xs text-gray-400">#{user.id}</span>
                        <span className="mx-1.5">·</span>
                        {user.email}
                        {user.learn_worlds_user_id && (
                          <>
                            <span className="mx-1.5">·</span>
                            <span className="text-xs">LW: {user.learn_worlds_user_id}</span>
                          </>
                        )}
                        <span className="mx-1.5">·</span>
                        <span className="text-xs">Joined {user.created_at}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link
                      href={`/superadmin/users/${user.id}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 transition cursor-pointer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                      View
                    </Link>
                    {!user.superadmin && (
                      <button
                        type="button"
                        onClick={() => handleImpersonate(user)}
                        disabled={pendingId === `impersonate-${user.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition cursor-pointer disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Impersonate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
